import moment from 'moment';
import { useLayoutEffect, useRef } from 'react';
import { useLegacyDidUpdate, useLegacyState } from './useLegacyState';
import {
  copyUnassignedTask,
  copyWeek,
  getDayWiseTotal as getDayWiseTotalRequest,
  getEmployeeLeaveHolidayData,
  getFreezeDateValue,
  getPastWeekLeaveMismatchesData,
  getProjectList as getProjectListRequest,
  getTimeSheetList,
  saveOrSubmit,
  taskDonePatch,
} from '../services/apiClient';
import { createInitialTimeEntryState, type TimeEntryState } from '../state/timeEntryState';
import { toErrorState } from '../utils/errorMessage';
import { inputValidation, inputValidationOnBlur } from '../utils/validations';
import {
  calculateTimePercentage,
  createListofDays,
  formatNumberToTwoDecimals,
  getMaximumNavigateDate,
  getSortedListByDate,
  setDateAsNumber,
  weekAndYearConverter,
} from '../utils/week';
import type {
  AlertData,
  Day,
  ErrorState,
  LoggedUser,
  ProjectListItem,
  RightPanelDivClassNameProperty,
  SelectedDate,
  SelectedTaskValue,
  TaskData,
  TimeDateEntry,
  TimeEntryCell,
  TimeSheet,
} from '../types';

/**
 * Every method of `mainMenu.jsx` (2,850 lines), as one hook.
 *
 * ## Why one hook and not the nine the plan sketched
 *
 * The plan proposed splitting this into `useWeekSelection`, `useProjectData`,
 * `useTimeSheetData`, `useTaskActions`, `useSaveSubmit`, `useCopyWeek`, `useUnfreeze`,
 * `useLeaveValidation` and `useAlerts`. Reading the class end to end shows why that shape
 * does not survive contact with the code: the call graph is densely cyclic across every
 * one of those proposed seams. `saveDraft` calls `refreshTimeSheetData` (data) and
 * `saveWIPDoneValueChanges` (task actions); `getResponseAndDoRefresh` calls
 * `refreshTimeSheetData`; `refreshTimeSheetData` calls `updateStateOfTimeSheetDataList`
 * (editing) and `getDayWiseTotal` (data); `copyWeekButton` calls `getUserTimeSheetData`.
 * Splitting them would mean wiring hooks to each other through refs and late binding —
 * indirection that buys nothing and is exactly where a bug-for-bug port goes wrong.
 *
 * What DID come out cleanly is in `utils/week.ts` (the pure date and arithmetic helpers)
 * and `state/timeEntryState.ts` (the state shape). `components/timeentry/MainMenu.tsx` is
 * then render-only. That is the honest decomposition of this particular class.
 *
 * ## `stateRef.current`, not `state`
 *
 * The class read `this.state` in every method, which is always the last committed state,
 * including inside `.then()` continuations that resolve several renders later. `state`
 * captured in a closure would be that render's value. `stateRef.current` is the exact
 * equivalent, so it is used throughout — see hooks/useLegacyState.ts.
 *
 * `propsRef.current` is here for the same reason and stands in for `this.props`.
 *
 * ## What was dropped
 *
 * Provably-unreachable code that `noUnusedLocals` would reject anyway:
 * - `getNonProjectTask()` and `getGlobalTasks()` — neither is called from anywhere.
 *   Dropping them drops the `getMockNonProjectTask` import and the `getGlobalTasks`
 *   request, which never fired. `state.nonProjectTask` therefore stays `[]`, as it does
 *   today, and is still passed to the right panel.
 * - The `getMockUserTimeSheetData` / `getMockProjectList` / `getMockUnassignedTasks` /
 *   `getMockDayWiseTotal` imports (jsx:8-14) — none was referenced even before that.
 * - The `getUnassignedTasks`, `getAlerts`, `validation1`, `submitSaveDraftPatchMethod` and
 *   `submitSaveOrSubmitPatchMethod` imports — never referenced. The last two are not
 *   exported by utils.js at all and were `undefined`.
 * - `nextArrow` / `backArrow` image imports — the buttons are styled from App.css.
 * - `let status = false` in `saveWIPDoneValueChanges` and `let calender = …` in
 *   `handleCalender` — assigned, never read.
 * - The second parameter of `handleSuccessOrErrorStatus`; its only use is commented out.
 *
 * The duplicate `weekAndYearConverter` (declared twice, jsx:1794 and jsx:1870, identical
 * bodies) is a single function in `utils/week.ts`.
 */

/* ------------------------------- small helpers ------------------------------- */

/**
 * `a > b` exactly as JavaScript evaluates it.
 *
 * The sort comparators below compare fields that are optional (`projectName`), or that no
 * interface declares at all and so arrive as `unknown` through an index signature
 * (`timeTypeId`). TypeScript rejects `>` on both. The coercion is isolated here rather
 * than scattered as casts, and the emitted comparison is byte-for-byte the original's.
 */
function gt(a: unknown, b: unknown): boolean {
  return (a as number) > (b as number);
}

/** `.sort((a, b) => (a.timeTypeId > b.timeTypeId ? 1 : -1))`, written once. */
function byTimeTypeId(a: TimeEntryCell, b: TimeEntryCell): number {
  return gt(a.timeTypeId, b.timeTypeId) ? 1 : -1;
}

/**
 * The two-key ordering `readNFetchUserTimeSheetDataList` and
 * `updateStateOfTimeSheetDataList` both apply: alphabetical by project name, then the
 * synthetic "Non Project" block (`gteProjectId === -1`) forced to the end.
 *
 * The second `.sort` returns 0 for most pairs, which is not a total order and therefore
 * depends on the engine's sort stability. V8's sort has been stable since 2018, so this is
 * deterministic in practice — and it is what runs today. Reproduced, not rewritten.
 */
function sortTimeSheets(list: TimeSheet[]): TimeSheet[] {
  return list
    .sort((a, b) => (gt(a.projectName, b.projectName) ? 1 : -1))
    .sort((a, b) => (a.gteProjectId !== -1 && b.gteProjectId === -1 ? -1 : 0));
}

/** One project's timesheet for one week, tagged so a stale response can be discarded. */
interface FetchedTimeSheet {
  userTimeSheetData: TimeSheet;
  year: number;
  week: number;
  operationalUserGteEmployeeNo: number;
}

/**
 * `utils/services.js:fetchData`, inlined.
 *
 * services.js was a three-function file wrapping already-promise-returning calls in
 * `new Promise((resolve) => resolve(...))`, which adds a microtask and nothing else. The
 * wrapper is dropped; the tagging of the response with the year/week/employee it was asked
 * for is the part that matters and is kept, because `readUserTimeSheetData` uses it to
 * throw away responses for a week the user has already navigated away from.
 */
function fetchTimeSheet(
  gteProjectId: number,
  year: number,
  week: number,
  operationalUserGteEmployeeNo: number,
  loggedUserGteEmployeeNo: number,
  currentDate: string | undefined,
): Promise<FetchedTimeSheet> {
  return getTimeSheetList(
    {
      gteEmployeeNo: operationalUserGteEmployeeNo,
      year: year,
      weekNo: week,
      loggedUserGteEmployeeNo: loggedUserGteEmployeeNo,
    },
    gteProjectId,
    currentDate,
  ).then((res) => ({
    userTimeSheetData: res as unknown as TimeSheet,
    year: year,
    week: week,
    operationalUserGteEmployeeNo: operationalUserGteEmployeeNo,
  }));
}

/** `utils/services.js:updateTaskDone`, inlined for the same reason. */
function updateTaskDone(
  taskAssignmentId: number,
  value: unknown,
): Promise<{ taskAssignmentId: number; response: unknown }> {
  return taskDonePatch(taskAssignmentId, value).then((res) => ({
    taskAssignmentId: taskAssignmentId,
    response: res,
  }));
}

/* ------------------------------ upstream shapes ------------------------------ */

interface LeaveHolidayDay {
  weekDate?: string;
  isHoliday?: string;
  isLeave?: string;
  hourlyLeaveCount?: number;
}

interface DayTotalItem {
  date: string;
  dayTotal: number;
}

/** The submit response is EITHER a list of validation errors OR `{ message }`. */
interface SubmitErrorEntry {
  maximumHourExceedError?: Record<string, unknown>[];
  minimumHourExceedError?: Record<string, unknown>[];
  invoiceCommentMissingError?: Record<string, unknown>[];
}

/* ------------------------------------ props ----------------------------------- */

export interface TimeEntryControllerProps {
  /** App-level error slice (App.js:187). A change to it clears loading and mirrors in. */
  error: ErrorState;
  alertData: AlertData | null;
  loggedUserDetails: LoggedUser | null;
  loggedUserGteEmployeeNo: number;
  operationalUserGteEmployeeNo: number;
  /**
   * Was `handleSaveDraft` (App.js:193) — a BOOLEAN prop whose name collided with the
   * method of the same name. Renamed so both can coexist in one scope; nothing else about
   * it changed. NavBar sets it before navigating away, and the one-shot
   * `isHandleSaveDraftCalled` latch stops it from firing on every subsequent render.
   */
  handleSaveDraftRequested: boolean;
  clickedAlert: boolean;
  updateStateOfClickedAlert: (value: boolean) => void;
  /** Was the prop `selectedDate` (App.js:198) — a callback, renamed away from the state key. */
  updateSelectedDate: (date: SelectedDate) => void;
  setAlerts: (operationalUserGteEmployeeNo: number) => void;
  /**
   * `envConfig.banner.showBanner` / `envConfig.showFeedback`, which the class read from the
   * bundled property.json inside its constructor. They now arrive from
   * `GET /api/auth-config` and are passed in so this hook has no config dependency of its
   * own. Read once, at mount, exactly as the constructor read them.
   */
  showBannerInitially: boolean;
  showFeedbackInitially: boolean;
}

export function useTimeEntryController(props: TimeEntryControllerProps) {
  const propsRef = useRef(props);
  propsRef.current = props;

  const { state, stateRef, setState } = useLegacyState<TimeEntryState>(() =>
    createInitialTimeEntryState({
      // The old app read these from the bundled property.json at construction time; they
      // now come from GET /api/auth-config, which has resolved before MainMenu mounts.
      showBanner: props.showBannerInitially,
      showFeedback: props.showFeedbackInitially,
    }),
  );

  /** `this.calendarRef` — attached to the date button, read by `handleClickOutside`. */
  const calendarRef = useRef<HTMLButtonElement>(null);

  /* --------------------------------- fetching --------------------------------- */

  const setSelectedDelegateUser = (): void => {
    setState({
      gteEmployeeNo: propsRef.current.operationalUserGteEmployeeNo,
      loggedUserGteEmployeeNo: propsRef.current.loggedUserGteEmployeeNo,
      operationalUserGteEmployeeNo: propsRef.current.operationalUserGteEmployeeNo,
    });
  };

  const getEmployeeLeaveHolidayList = (): void => {
    const { loggedUserGteEmployeeNo, operationalUserGteEmployeeNo } = propsRef.current;

    if (loggedUserGteEmployeeNo > 0 && operationalUserGteEmployeeNo > 0) {
      getEmployeeLeaveHolidayData({
        gteEmployeeNo: operationalUserGteEmployeeNo,
        year: stateRef.current.selectedDate.year,
        weekNo: stateRef.current.selectedDate.week,
        loggedUserGteEmployeeNo: loggedUserGteEmployeeNo,
      })
        .then((response) => {
          const data = (
            response as { employeeLeaveHoliday?: { weekData?: LeaveHolidayDay[] } }
          )?.employeeLeaveHoliday?.weekData;
          const selectedDate = stateRef.current.selectedDate;
          const days = stateRef.current.selectedDate?.days?.map((day) => {
            return {
              ...day,
              isHoliday: data?.find((element) => element?.weekDate === day.date)?.isHoliday,
              isLeave: data?.find((element) => element?.weekDate === day.date)?.isLeave,
              hourlyLeaveCount: data?.find((element) => element?.weekDate === day.date)?.hourlyLeaveCount || 0,
            };
          });

          setState({
            selectedDate: { ...selectedDate, days: days },
          });
        })
        .catch((error) => {
          setState({
            loading: false,
            error: toErrorState(error),
            projectList: [],
            userTimeSheetData: [],
          });
        });
    }
  };

  const getProjectList = (): void => {
    setState({
      loading: true,

      rightPanelDivClassNameProperty: {
        alerts: {
          button: 'accordion-button collapsed ',
          div: 'accordion-collapse collapse',
        },
        addTask: {
          button: 'accordion-button collapsed',
          div: 'accordion-collapse collapse',
        },
        selectedTask: {
          button: 'accordion-button',
          div: 'accordion-collapse collapse show',
        },
      },
    });

    const { loggedUserGteEmployeeNo, operationalUserGteEmployeeNo } = propsRef.current;

    if (loggedUserGteEmployeeNo > 0 && operationalUserGteEmployeeNo > 0) {
      getPastWeekLeaveMismatches();
      getProjectListRequest({
        gteEmployeeNo: operationalUserGteEmployeeNo,
        year: stateRef.current.selectedDate.year,
        weekNo: stateRef.current.selectedDate.week,
        loggedUserGteEmployeeNo: loggedUserGteEmployeeNo,
      })
        .then((response) => {
          /*
           * Two setState calls inside one continuation, exactly as jsx:453 and jsx:476.
           * They coalesce into a single commit; splitting or merging them would change
           * the number of renders, so they stay as written.
           */
          setState({
            rightPanelDivClassNameProperty: {
              alerts: {
                button: 'accordion-button collapsed ',
                div: 'accordion-collapse collapse',
              },
              addTask: {
                button: 'accordion-button collapsed',
                div: 'accordion-collapse collapse',
              },
              selectedTask: {
                button: 'accordion-button',
                div: 'accordion-collapse collapse show',
              },
            },
            selectedTaskValue: {},
          });

          /**
           * {...project,isSelected:true} -> req fr project drop down checkboxes
           */
          const projects = response as unknown as ProjectListItem[] | undefined;
          // `.map` on a possibly-undefined value and then `.sort` on the result with no
          // guard — as in the original, where a missing body throws rather than rendering
          // an empty week.
          const projectList = projects?.map((project) => {
            return { ...project, isSelected: true }; // initially all should be selected
          }) as ProjectListItem[];

          setState({
            projectList: projectList.sort((a, b) => (gt(a.name, b.name) ? 1 : -1)),
            isCopyUnassigedTaskEnabled:
              projectList?.find(
                ({ isCopyUnassigedTaskEnabled }) => isCopyUnassigedTaskEnabled === 'Y',
              ) && true,
          });

          propsRef.current.setAlerts(propsRef.current.operationalUserGteEmployeeNo);
          getUserTimeSheetData(projects);
        })
        .catch((error) => {
          setState({
            loading: false,
            error: toErrorState(error),
            projectList: [],
            userTimeSheetData: [],
          });
        });
    }
  };

  /** No `.catch` here in the original either — a failure rejects unhandled. */
  const getDayWiseTotal = (): void => {
    getDayWiseTotalRequest({
      gteEmployeeNo: propsRef.current.operationalUserGteEmployeeNo,
      year: stateRef.current.selectedDate.year,
      weekNo: stateRef.current.selectedDate.week,
      loggedUserGteEmployeeNo: propsRef.current.loggedUserGteEmployeeNo,
    }).then((response) => {
      const data = response as unknown as DayTotalItem[] | undefined;
      const selectedDate = stateRef.current.selectedDate;
      const days = stateRef.current.selectedDate?.days?.map((day) => {
        let timeTotal = 0;
        data?.map((daytotal) => {
          if (day.date === daytotal.date) {
            timeTotal = timeTotal + daytotal.dayTotal;
          }
        });
        return {
          ...day,
          timeTotal: Math.round((timeTotal + Number.EPSILON) * 100) / 100,
          timeError: parseFloat(String(timeTotal)) > 24 ? true : false,
        };
      });

      setState({
        selectedDate: { ...selectedDate, days: days },
      });
    });
  };

  /**
   * Note the read-then-act on the very next line: `freezeDateData` is mutated IN PLACE and
   * handed to setState, so `stateRef.current.freezeDateData.isUpdated` is already true
   * when the `if` runs, despite the commit not having happened. That is what makes
   * `validateFreezeDate()` fire on the first load, and it works here for the same reason
   * it worked in the class — the object in state is the same object.
   */
  const getFreezeDate = (): void => {
    getFreezeDateValue()
      .then((response) => {
        const freezeDateData = stateRef.current.freezeDateData;
        freezeDateData.configValue = moment(
          (response as unknown as { configValue?: string }[])[0]?.configValue,
        ).format('YYYY-MM-DD');
        freezeDateData.isUpdated = true;

        setState({
          freezeDateData: freezeDateData,
        });
        if (stateRef.current.freezeDateData.isUpdated) {
          validateFreezeDate();
        }
      })
      .catch((error) => {
        setState({
          loading: false,
          error: toErrorState(error),
        });
      });
  };

  const getPastWeekLeaveMismatches = (): void => {
    getPastWeekLeaveMismatchesData(
      {
        gteEmployeeNo: propsRef.current.operationalUserGteEmployeeNo,
        year: stateRef.current.selectedDate?.year,
        weekNo: stateRef.current.selectedDate?.week,
        loggedUserGteEmployeeNo: propsRef.current.loggedUserGteEmployeeNo,
      },
      stateRef.current.selectedDate?.currentDate?.toString(),
    )
      .then((response) => {
        const body = response as {
          status?: string;
          message?: string;
          dates?: string[];
        };
        // Mutated in place, as in the original — TimeEntryMainPage holds this same object.
        const pastWeekLeaveMismatchesDetails = stateRef.current.pastWeekLeaveMismatchesDetails;
        pastWeekLeaveMismatchesDetails.status = body?.status;
        pastWeekLeaveMismatchesDetails.message = body?.message;
        pastWeekLeaveMismatchesDetails.dates = body?.dates;
        pastWeekLeaveMismatchesDetails.isUpdated = true;

        setState({
          pastWeekLeaveMismatchesDetails: pastWeekLeaveMismatchesDetails,
        });
      })
      .catch((error) => {
        setState({
          loading: false,
          error: toErrorState(error),
        });
      });
  };

  /**
   * Decides whether the displayed week is frozen, by comparing it against the configured
   * freeze date's own ISO week/year. A week at or after the freeze date is frozen.
   *
   * Note there is no `else` covering "same year, same week" beyond the `>=` branch, and no
   * branch at all when `year` is NaN — the flag then simply keeps its previous value.
   */
  const validateFreezeDate = (): void => {
    const weekNYear = weekAndYearConverter(stateRef.current.freezeDateData?.configValue);

    const week = parseInt(weekNYear?.W);
    const year = parseInt(weekNYear?.Y);

    const freezeDateData = stateRef.current.freezeDateData;
    if (year === Number(stateRef.current.selectedDate.year)) {
      if (
        week === Number(stateRef.current.selectedDate.week) ||
        week > Number(stateRef.current.selectedDate.week)
      ) {
        freezeDateData.isFreezed = true;
      }
      if (week < Number(stateRef.current.selectedDate.week)) {
        freezeDateData.isFreezed = false;
      }
    }
    if (Number(stateRef.current.selectedDate.year) < year) {
      freezeDateData.isFreezed = true;
    }
    if (Number(stateRef.current.selectedDate.year) > year) {
      freezeDateData.isFreezed = false;
    }
    setState({
      freezeDateData: freezeDateData,
    });
  };

  /* ------------------------- timesheet load and refresh ------------------------ */

  const updateSelectedTaskValue = (selectedTaskValue: SelectedTaskValue): void => {
    setState({
      selectedTaskValue: selectedTaskValue,
    });
  };

  /**
   * `selectedDate` is typed `Partial<SelectedDate>` because `refreshTimeSheetData({}, 0, 0,
   * true)` is a real call site (jsx:2027, :2084, :2158). Its fields are only read on the
   * `updateAllSheet === false` path, which is never reached with `{}`.
   */
  const refreshTimeSheetData = (
    selectedDate: Partial<SelectedDate>,
    operationalUserGteEmployeeNo: number,
    gteProjectId: number,
    updateAllSheet: boolean,
  ): void => {
    setState({
      loading: true,
      saveDraft: false,
    });
    const promises: Promise<FetchedTimeSheet>[] = [];
    if (updateAllSheet) {
      stateRef.current.userTimeSheetData?.map((sheet) => {
        promises.push(
          fetchTimeSheet(
            sheet?.gteProjectId,
            stateRef.current.selectedDate?.year,
            stateRef.current.selectedDate?.week,
            stateRef.current.operationalUserGteEmployeeNo,
            propsRef.current.loggedUserGteEmployeeNo,
            stateRef.current.selectedDate?.currentDate?.toString(),
          ),
        );
      });
    } else {
      promises.push(
        fetchTimeSheet(
          gteProjectId,
          selectedDate.year as number,
          selectedDate.week as number,
          operationalUserGteEmployeeNo,
          propsRef.current.loggedUserGteEmployeeNo,
          stateRef.current.selectedDate?.currentDate?.toString(),
        ),
      );
    }

    if (promises?.length > 0) {
      readUserTimeSheetData(promises, updateAllSheet);
      getDayWiseTotal();
    }
  };

  /**
   * The stale-week guard: a response is only applied if it is still for the week on
   * screen. This is what makes rapid week navigation safe without an AbortController.
   */
  const readUserTimeSheetData = (
    promises: Promise<FetchedTimeSheet>[],
    updateAllSheet: boolean,
  ): void => {
    Promise.all(promises).then((promise) => {
      const newObjList: TimeSheet[] = [];
      promise?.map((obj) => {
        if (
          obj.week === stateRef.current.selectedDate.week &&
          obj.year === stateRef.current.selectedDate.year
        ) {
          if (updateAllSheet) {
            const taskData = updateTaskDataList(obj.userTimeSheetData.taskData);

            newObjList.push({ ...obj.userTimeSheetData, taskData: taskData });
          } else {
            addTaskToTimeSheet(obj);
          }
        }
      });

      if (newObjList.length > 0) {
        updateUserTimeSheetDataAtSubmitOrSave(newObjList);
      }
    });
  };

  /**
   * Replaces every project's task list from a full refresh.
   *
   * A project absent from `newObjList` gets `taskData: []`, not its previous tasks — the
   * inner loop starts from an empty array and only assigns on a match. That is deliberate
   * in the original and is why a refresh of one week never leaves rows from another.
   */
  const updateUserTimeSheetDataAtSubmitOrSave = (newObjList: TimeSheet[]): void => {
    const userTimeSheetDataS = stateRef.current.userTimeSheetData;
    const userTimeSheetData = userTimeSheetDataS?.map((sheet) => {
      let taskData: TaskData[] = [];
      newObjList?.map((obj) => {
        if (obj.gteProjectId === sheet.gteProjectId) {
          taskData = obj.taskData as TaskData[];
        }
      });

      return {
        ...sheet,
        taskData: taskData,
      };
    });

    updateStateOfTimeSheetDataList(userTimeSheetData);
    const selectedTaskValue = setSelectedTaskValue(userTimeSheetData);
    updateStateOfSelectedTaskValue(selectedTaskValue);
  };

  /**
   * Re-points the selected-task panel at the freshly-loaded copies of the same project,
   * task and day. Mutates `selectedTaskValue` in place and returns it.
   *
   * `.task.taskId` and `.timedate.date` are read WITHOUT optional chaining here (jsx:753,
   * jsx:757), so a selection missing either throws. That is only reachable when a
   * timesheet whose `gteProjectId` matches is loaded while nothing is selected, which the
   * `gteProjectId` comparison against an empty `selectedTaskValue` prevents. The
   * non-null assertions below mark exactly where the original would throw.
   */
  const setSelectedTaskValue = (userTimeSheetData: TimeSheet[]): SelectedTaskValue => {
    const selectedTaskValue = stateRef.current.selectedTaskValue;

    userTimeSheetData?.map((timeSheet) => {
      if (timeSheet.gteProjectId === selectedTaskValue.gteProjectId) {
        selectedTaskValue.timeSheet = timeSheet;

        timeSheet?.taskData?.map((taskdata) => {
          if (taskdata.taskId === selectedTaskValue.task!.taskId) {
            selectedTaskValue.task = taskdata;

            taskdata?.timeDate?.map((timedate) => {
              if (timedate.date === selectedTaskValue.timedate!.date) {
                selectedTaskValue.timedate = timedate;
              }
            });
          }
        });
      }
    });

    return selectedTaskValue;
  };

  /** Single-project refresh — used after adding or deleting a task. */
  const addTaskToTimeSheet = (newTimeSheetObj: FetchedTimeSheet): void => {
    if (
      newTimeSheetObj.operationalUserGteEmployeeNo === stateRef.current.operationalUserGteEmployeeNo
    ) {
      const taskData = updateTaskDataList(newTimeSheetObj.userTimeSheetData.taskData);
      const userTimeSheetData = addTaskListToUserTimeSheet(
        taskData,
        newTimeSheetObj.userTimeSheetData.gteProjectId,
      );
      updateStateOfTimeSheetDataList(userTimeSheetData);
    }
  };

  /**
   * Normalises one project's tasks after a refresh: week total per task, the
   * "has an approved or frozen entry" flag that decides whether the trash icon shows, and
   * the per-day `istimeTypeEnableProject` flag that switches a day cell between a single
   * input and the multi-time-type panel.
   *
   * `istimeTypeEnableProject` is declared OUTSIDE the task loop and never reset, so once
   * any task in the project has a multi-entry day, every task processed afterwards
   * inherits `true`. `updateInitialTimeSheetDataList` below declares the same variable
   * INSIDE the loop, so the two paths disagree. Both are reproduced as written — this is a
   * real inconsistency in the original, not a transcription slip.
   */
  const updateTaskDataList = (taskData: TaskData[] | undefined): TaskData[] => {
    const currentWeekDaysList = stateRef.current.selectedDate?.days as Day[];
    let istimeTypeEnableProject = false;
    const taskdata = taskData?.map((task) => {
      let totalTimeForTask = 0;
      let isTimeEntryStatusAsA = false;
      const timeDate = task?.timeDate?.map((timeentrydate) => {
        if (
          currentWeekDaysList.some(
            (currentWeekDaysList) => currentWeekDaysList.date === timeentrydate.date,
          )
        ) {
          // Straight from the BFF here, so always a number.
          totalTimeForTask = totalTimeForTask + (timeentrydate.totalTime as number);
        }

        const timeEntry = timeentrydate?.timeEntry?.map((entry) => {
          if (entry.timeEntryStatus === 'A' || entry.isFreezed === 'Y') {
            isTimeEntryStatusAsA = true;
          }

          return {
            ...entry,
            timeError: false,
            isChanged: false,
            focused: false,
            initialTime: entry?.time,
            initialInvoiceComment: entry?.invoiceComment,
          };
        });

        if ((timeentrydate.timeEntry?.length ?? 0) > 1) {
          istimeTypeEnableProject = true;
        }
        return {
          ...timeentrydate,
          timeError: false,
          istimeTypeEnableProject: (timeentrydate.timeEntry?.length ?? 0) > 1 ? true : false,
          timeEntry: (timeEntry as TimeEntryCell[]).sort(byTimeTypeId),
        };
      });
      const estTimePersentage = calculateTimePercentage(
        task?.loggedHours as number,
        task?.estimatedHours as number,
      );
      return {
        ...task,
        timeDate: timeDate,
        totalTimeForTask: totalTimeForTask,
        isTimeEntryStatusAsA: isTimeEntryStatusAsA,
        isRemoved: false,
        isChanged: false,
        isChangedWIPStatus: false,
        istimeTypeEnableProject: istimeTypeEnableProject,
        estTimePersentage: estTimePersentage,
      };
    });
    return taskdata as TaskData[];
  };

  const addTaskListToUserTimeSheet = (taskData: TaskData[], gteProjectId: number): TimeSheet[] => {
    const userTimeSheetData = stateRef.current.userTimeSheetData?.map((timeSheet) => {
      if (timeSheet.gteProjectId === gteProjectId) {
        return {
          ...timeSheet,
          taskData: taskData,
          display:
            timeSheet.gteProjectId === -1 ? true : (taskData?.length ?? 0) > 0 ? true : false,

          viewOnLoad: true,
        };
      } else {
        return timeSheet;
      }
    });
    return userTimeSheetData;
  };

  /**
   * Fans out one timesheet request per project, then folds the responses together.
   *
   * Called with two different shapes — the project list from `getProjectList`, and the
   * loaded timesheets after a copy-week — so the parameter names only what both provide.
   */
  const getUserTimeSheetData = (projects: { gteProjectId: number }[] | undefined): void => {
    const promises: Promise<FetchedTimeSheet>[] = [];
    projects?.map((project) => {
      promises.push(
        fetchTimeSheet(
          project.gteProjectId,
          stateRef.current.selectedDate.year,
          stateRef.current.selectedDate.week,
          propsRef.current.operationalUserGteEmployeeNo,
          propsRef.current.loggedUserGteEmployeeNo,
          stateRef.current.selectedDate?.currentDate?.toString(),
        ),
      );
    });
    getDayWiseTotal();
    readNFetchUserTimeSheetDataList(promises);
  };

  const readNFetchUserTimeSheetDataList = (promises: Promise<FetchedTimeSheet>[]): void => {
    Promise.all(promises).then((promise) => {
      let userTimeSheetData: TimeSheet[] = [];
      promise?.map((obj) => {
        if (
          obj.week === stateRef.current.selectedDate.week &&
          obj.year === stateRef.current.selectedDate.year
        ) {
          userTimeSheetData = [...userTimeSheetData, obj.userTimeSheetData];
        }
      });
      if (userTimeSheetData.length > 0) {
        // // set project name alphabet order
        updateInitialTimeSheetDataList(sortTimeSheets(userTimeSheetData));
      }
    });
  };

  const getProjectIds = (): void => {
    const projectIds = stateRef.current.projectList
      ?.map((project) => project.gteProjectId)
      .filter((projectId) => projectId !== -1)
      .join(',');
    setState({ projectListIds: projectIds });
  };

  /**
   * calculate and add the value of  totalTimeForTask of week
   *
   * Also decides which project blocks render at all:
   * - `display` false hides the block. A project whose tasks are ALL
   *   `timeSheetRecordStatus === "H"` is hidden, but the synthetic Non Project block
   *   (`gteProjectId === -1`) is always shown, tasks or not.
   * - `viewOnLoad` is a second, independent gate, true when the project has tasks or is
   *   that -1 block.
   */
  const updateInitialTimeSheetDataList = (initialData: TimeSheet[]): void => {
    const currentWeekDaysList = stateRef.current.selectedDate?.days as Day[];

    const userTimeSheetData = initialData?.map((project) => {
      /**
       *  if all task's task?.timeSheetRecordStatus === "H"  -> this project hide in timesheet [should not display in timesheet]
       *  But For Virtusa NON Project [gteProjectId === -1] -> it should not be applicable [Virtusa Non Project should display regardless of task exist or not]
       */

      let isAllTasksAsTimeSheetRecordStatus_H = true;
      const taskData = project.taskData?.map((task) => {
        let totalTimeForTask = 0;
        let isTimeEntryStatusAsA = false;
        let istimeTypeEnableProject = false;

        // if exist one of timeSheetRecordStatus !== "H" ->this project display in timesheet
        if (!(task?.timeSheetRecordStatus === 'H')) {
          isAllTasksAsTimeSheetRecordStatus_H = false;
        }

        const timeDate = task?.timeDate?.map((timeentrydate) => {
          if (
            currentWeekDaysList.some(
              (currentWeekDaysList) => currentWeekDaysList.date === timeentrydate.date,
            )
          ) {
            totalTimeForTask = totalTimeForTask + (timeentrydate.totalTime as number);
          }

          const timeEntry = timeentrydate?.timeEntry?.map((entry) => {
            if (entry.timeEntryStatus === 'A' || entry.isFreezed === 'Y') {
              isTimeEntryStatusAsA = true;
            } //required for TRASH icon appear on time sheet
            return {
              ...entry,
              timeError: false,
              isChanged: false,
              isSelected: false,
              focused: false,
              initialTime: entry?.time,
              initialInvoiceComment: entry?.invoiceComment,
            };
          });

          if ((timeentrydate.timeEntry?.length ?? 0) > 1) {
            istimeTypeEnableProject = true;
          }

          return {
            ...timeentrydate,
            timeError: false,
            istimeTypeEnableProject: (timeentrydate.timeEntry?.length ?? 0) > 1 ? true : false,
            timeEntry: (timeEntry as TimeEntryCell[]).sort(byTimeTypeId),
            isSelected: false,
          };
        });
        const estTimePersentage = calculateTimePercentage(
          task?.loggedHours as number,
          task?.estimatedHours as number,
        );
        return {
          ...task,
          timeDate: timeDate,
          totalTimeForTask: totalTimeForTask,
          isTimeEntryStatusAsA: isTimeEntryStatusAsA,
          isRemoved: false,
          isChanged: false,
          isChangedWIPStatus: false,
          istimeTypeEnableProject: istimeTypeEnableProject,
          estTimePersentage: estTimePersentage,
        };
      });

      return {
        ...project,
        taskData: taskData,

        isViewed: true,
        isChanged: false,
        viewOnLoad:
          (taskData?.length ?? 0) > 0 ? true : project.gteProjectId === -1 ? true : false,
        display:
          project.gteProjectId === -1
            ? true
            : (taskData?.length ?? 0) > 0
              ? isAllTasksAsTimeSheetRecordStatus_H
                ? false
                : true
              : false,
      };
    });

    setState({
      userTimeSheetData: userTimeSheetData as TimeSheet[],
      loading: false,
      initialLoading: false,
    });
  };

  /* ------------------------------ week navigation ------------------------------ */

  /**
   * Applies a date chosen in the calendar popup.
   *
   * The `add(1, "days")` is the original's compensation for the picker handing back the
   * previous day (jsx:1054-1057) — a timezone artefact of `toISOString()` in the Calender
   * component. Both halves are preserved together; removing either alone shifts the week.
   */
  const selectedDatePicker = (): void => {
    const selectedDate = stateRef.current.selectedDate;
    let reqDate = stateRef.current.datePicker;

    /**
     * date picker take the previous date as picked date
     * for get accurate date: add one day for picked up date
     */
    reqDate = moment(reqDate).add(1, 'days').format('YYYY-MM-DD');
    const firstDayofCurrentWeek = moment(reqDate).startOf('isoWeek').format('YYYY-MM-DD');
    const weekNYear = weekAndYearConverter(firstDayofCurrentWeek);
    setState({
      selectedDate: {
        ...selectedDate,
        today: moment().format('YYYY-MM-DD'),
        firstDayofCurrentWeek: moment(reqDate).startOf('isoWeek').format('YYYY-MM-DD'),
        firstDayofWeekForUnfreeze: moment(reqDate).startOf('isoWeek').format('DD-MMM-YYYY'),
        year: parseInt(weekNYear?.Y),
        week: parseInt(weekNYear?.W),
        dateOfFirstDayofCurrentWeek: moment(reqDate).startOf('isoWeek').format('D MMMM'),
        dateOfLastDayofCurrentWeek: moment(reqDate).endOf('isoWeek').format('D MMMM'),
        days: createListofDays(moment(reqDate).startOf('isoWeek').format('YYYY-MM-DD')),
      },
      selectedTaskTab_class: 'accordion-collapse collapse show',
    });
  };

  const pastWeek = (): void => {
    const selectedDate = stateRef.current.selectedDate;
    const reqDate = stateRef.current.selectedDate.firstDayofCurrentWeek;
    const dateOfLastMondy = moment(reqDate).subtract(7, 'days').format('YYYY-MM-DD');
    const weekNYear = weekAndYearConverter(dateOfLastMondy);
    setState({
      loading: true,
      datePicker: dateOfLastMondy,
      selectedDate: {
        ...selectedDate,
        today: moment().format('YYYY-MM-DD'),
        firstDayofCurrentWeek: dateOfLastMondy,
        firstDayofWeekForUnfreeze: moment(dateOfLastMondy).startOf('isoWeek').format('DD-MMM-YYYY'),
        year: parseInt(weekNYear?.Y),
        week: parseInt(weekNYear?.W),
        dateOfFirstDayofCurrentWeek: moment(dateOfLastMondy).startOf('isoWeek').format('D MMMM'),
        dateOfLastDayofCurrentWeek: moment(dateOfLastMondy).endOf('isoWeek').format('D MMMM'),

        days: createListofDays(dateOfLastMondy),
      },
    });
  };

  const nextWeek = (): void => {
    const selectedDate = stateRef.current.selectedDate;
    const reqDate = stateRef.current.selectedDate.firstDayofCurrentWeek;

    const dateOfNextMonday = moment(reqDate).add(7, 'days').format('YYYY-MM-DD');
    const weekNYear = weekAndYearConverter(dateOfNextMonday);

    setState({
      loading: true,
      datePicker: dateOfNextMonday,
      selectedDate: {
        ...selectedDate,
        today: moment().format('YYYY-MM-DD'),
        firstDayofCurrentWeek: dateOfNextMonday,
        firstDayofWeekForUnfreeze: moment(dateOfNextMonday)
          .startOf('isoWeek')
          .format('DD-MMM-YYYY'),
        year: parseInt(weekNYear?.Y),
        week: parseInt(weekNYear?.W),

        dateOfFirstDayofCurrentWeek: moment(dateOfNextMonday).startOf('isoWeek').format('D MMMM'),
        dateOfLastDayofCurrentWeek: moment(dateOfNextMonday).endOf('isoWeek').format('D MMMM'),
        days: createListofDays(dateOfNextMonday),
      },
    });
  };

  /* --------------------------------- grid edits -------------------------------- */

  /**
   * A time value typed into the WEEK GRID (one input per task per day).
   *
   * `type` is "timeChange" while typing and "timeValidation" on blur. The two branches
   * differ in three ways that all matter: blur additionally runs `inputValidationOnBlur`,
   * writes `focused: false`, and stores `0` rather than `""` for an emptied cell — the
   * empty string is kept during typing precisely because it is the input's displayed
   * value, and replacing it with 0 mid-edit would put a "0" under the caret.
   */
  const timSlotValueUpdate = (
    rawValue: string,
    gteProjectId: number,
    task: TaskData,
    timedate: TimeDateEntry | undefined,
    type: 'timeChange' | 'timeValidation',
  ): void => {
    // `string | number`, not `string`: inputValidationOnBlur returns a NUMBER for a value
    // that contained a decimal point. Both forms flow onward from here, and both end up in
    // state — see TimeDateEntry.totalTime.
    let value: string | number = rawValue.replace(/[^0-9.]/g, '');
    value = inputValidation(value, 2);
    if (type === 'timeValidation') {
      value = inputValidationOnBlur(value);
    }
    const time = value === '' ? 0 : parseFloat(String(value));

    if (time > 24) {
      setState({
        clickInput: false,
      });
    } else {
      setState({
        clickInput: true,
      });
    }

    const userTimeSheetDataS = stateRef.current.userTimeSheetData;
    const selectedTaskValue = stateRef.current.selectedTaskValue;
    const userTimeSheetData = userTimeSheetDataS?.map((project) => {
      if (project.gteProjectId === gteProjectId) {
        const taskData = project.taskData?.map((taskdata) => {
          let totalTimeForTask = 0;
          if (taskdata?.taskAssignmentId === task?.taskAssignmentId) {
            const timeDate = taskdata?.timeDate?.map((timeentrydate) => {
              if (timeentrydate.date === timedate?.date) {
                totalTimeForTask = totalTimeForTask + parseFloat(String(time));
                updateDayWiseTotal(
                  timeentrydate.date,
                  timeentrydate.totalTime === '' ? 0 : parseFloat(String(timeentrydate.totalTime)),
                  time,
                );
                let timeEntry;
                if (!timeentrydate?.istimeTypeEnableProject) {
                  timeEntry = timeentrydate?.timeEntry?.map((timeEntryObj) => {
                    if (type === 'timeChange') {
                      return {
                        ...timeEntryObj,
                        time: time,
                        // isChanged: true,
                        isChanged: true,
                        focused: true,
                        timeError: value === '' ? false : gt(value, 24) ? true : false,
                      };
                    }
                    if (type === 'timeValidation') {
                      return {
                        ...timeEntryObj,
                        time: time,
                        isChanged: true,
                        focused: false,
                        timeError: value === '' ? false : gt(value, 24) ? true : false,
                      };
                    }
                    return undefined;
                  });
                } else {
                  timeEntry = timeentrydate?.timeEntry;
                }

                if (type === 'timeChange') {
                  return {
                    ...timeentrydate,
                    totalTime: value, //--> we cannot update 0 for "" value -> bcz this value take as time slot's input element's value
                    timeError:
                      !timeentrydate?.istimeTypeEnableProject &&
                      (value === '' ? false : gt(value, 24) ? true : false),
                    isChanged: true,
                    timeEntry: (timeEntry as TimeEntryCell[]).sort(byTimeTypeId),
                    isSelected: false,
                  };
                }
                if (type === 'timeValidation') {
                  return {
                    ...timeentrydate,
                    totalTime: value === '' ? 0 : value,
                    timeError: timeentrydate?.istimeTypeEnableProject
                      ? false
                      : value === ''
                        ? false
                        : gt(value, 24)
                          ? true
                          : false,
                    timeEntry: (timeEntry as TimeEntryCell[]).sort(byTimeTypeId),
                    isSelected: false,
                  };
                }
                return undefined;
              } else {
                const time = timeentrydate.totalTime === '' ? 0 : timeentrydate.totalTime;
                totalTimeForTask = totalTimeForTask + parseFloat(String(time));
                return { ...timeentrydate };
              }
            }) as TimeDateEntry[] | undefined;
            let loggedHours: number | string =
              (taskdata?.loggedHours as number) - taskdata?.totalTimeForTask;
            loggedHours = (loggedHours as number) + totalTimeForTask;
            loggedHours = formatNumberToTwoDecimals(loggedHours);
            const estTimePersentage = calculateTimePercentage(
              loggedHours,
              task?.estimatedHours as number,
            );
            if (type === 'timeChange') {
              selectedTaskValue.task = {
                ...taskdata,
                timeDate: timeDate,
                totalTimeForTask: totalTimeForTask,
                isChanged: true,
                loggedHours: loggedHours,
                estTimePersentage: estTimePersentage,
              };
              return {
                ...taskdata,
                timeDate: timeDate,
                totalTimeForTask: totalTimeForTask,
                isChanged: true,
                loggedHours: loggedHours,
                estTimePersentage: estTimePersentage,
              };
            }
            if (type === 'timeValidation') {
              selectedTaskValue.task = {
                ...taskdata,
                timeDate: timeDate,
                totalTimeForTask: totalTimeForTask,
                loggedHours: loggedHours,
                estTimePersentage: estTimePersentage,
              };
              return {
                ...taskdata,
                timeDate: timeDate,
                totalTimeForTask: totalTimeForTask,
                loggedHours: loggedHours,
                estTimePersentage: estTimePersentage,
              };
            }
            return undefined;
          } else {
            return taskdata;
          }
        }) as TaskData[] | undefined;
        if (type === 'timeChange') {
          return { ...project, taskData: taskData, isChanged: true };
        }
        if (type === 'timeValidation') {
          return { ...project, taskData: taskData };
        }
        return undefined;
      } else {
        return project;
      }
    }) as TimeSheet[];
    setState({
      userTimeSheetData: userTimeSheetData,
      saveDraft: true,
      selectedTaskValue: selectedTaskValue,
    });
  };

  /**
   * timeType_timSlotValueUpdate() Method
   *   -> here update state of "userTimeSheetData" list and "selectedTaskValue"
   *      which are used in timesheet panel and Selected task Panel
   *  -> within timeType_timSlotValueUpdate() ->
   *        -> call updateDayWiseTotal() method -> to update total time of day ->selected date
   *
   * This is the SELECTED TASK panel's editor, for projects that break a day into several
   * time types. Unlike the grid editor above, the day's `totalTime` is recomputed as the
   * sum of its entries rather than taken from the input.
   */
  const timeType_timSlotValueUpdate = (
    value: string | number,
    selectedTaskValue: SelectedTaskValue,
    entry: TimeEntryCell,
    timeError: boolean,
    type: 'timeChange' | 'timeValidation',
  ): void => {
    if (!timeError) {
      timeError = value !== '' && gt(value, 24) ? true : false;
    }

    if (timeError) {
      setState({
        clickInput: false,
      });
    } else {
      setState({
        clickInput: true,
      });
    }
    const userTimeSheetDataExit = stateRef.current.userTimeSheetData;

    const userTimeSheetData = userTimeSheetDataExit?.map((project) => {
      if (
        project.gteProjectId ===
        (selectedTaskValue?.timeSheet as TimeSheet | undefined)?.gteProjectId
      ) {
        const taskData = project?.taskData?.map((taskdata) => {
          let totalTimeForTask = 0;
          if (taskdata.taskId === selectedTaskValue.task!.taskId) {
            const timeDate = taskdata?.timeDate?.map((timedate) => {
              if (timedate.date === selectedTaskValue.timedate!.date) {
                let totalTime = 0;
                const timeEntry = timedate?.timeEntry?.map((timeentry) => {
                  if (timeentry.timeEntryId === entry.timeEntryId) {
                    const newTime = value === '' ? 0 : parseFloat(String(value));
                    totalTime = totalTime + newTime;
                    totalTimeForTask = totalTimeForTask + newTime;
                    updateDayWiseTotal(
                      timedate.date,
                      timeentry.time === '' ? 0 : parseFloat(String(timeentry.time)),
                      value === '' ? 0 : parseFloat(String(value)),
                    );

                    if (type === 'timeChange') {
                      return {
                        ...timeentry,
                        isChanged: true,
                        time: value,
                        timeError: timeError,
                        isSelected: false,
                        focused: true,
                      };
                    }
                    if (type === 'timeValidation') {
                      return {
                        ...timeentry,
                        isChanged: true,
                        time: value,
                        timeError: timeError,
                        isSelected: false,
                        focused: false,
                      };
                    }
                    return undefined;
                  } else {
                    totalTime = totalTime + parseFloat(String(timeentry.time));
                    totalTimeForTask = totalTimeForTask + parseFloat(String(timeentry.time));
                    return { ...timeentry, isSelected: false };
                  }
                }) as TimeEntryCell[];

                selectedTaskValue.newTimetotal = totalTime;
                selectedTaskValue.timedate = {
                  ...timedate,
                  isChanged: true,
                  timeEntry: timeEntry.sort(byTimeTypeId),
                  isSelected: false,
                };
                return {
                  ...timedate,
                  isSelected: false,
                  isChanged: true,
                  timeEntry: timeEntry.sort(byTimeTypeId),
                  totalTime: Math.round((totalTime + Number.EPSILON) * 100) / 100,
                };
              } else {
                const time = timedate.totalTime === '' ? 0 : timedate.totalTime;
                totalTimeForTask = totalTimeForTask + parseFloat(String(time));
                return timedate;
              }
            });

            let loggedHours: number | string =
              parseFloat(String(taskdata?.loggedHours)) -
              parseFloat(String(taskdata?.totalTimeForTask));
            loggedHours = (loggedHours as number) + parseFloat(String(totalTimeForTask));
            loggedHours = formatNumberToTwoDecimals(loggedHours);
            const estTimePersentage = calculateTimePercentage(
              loggedHours,
              taskdata?.estimatedHours as number,
            );
            selectedTaskValue.task = {
              ...taskdata,
              timeDate: timeDate,
              isSelected: false,
              totalTimeForTask: totalTimeForTask,
              isChanged: true,
              loggedHours: loggedHours,
              estTimePersentage: estTimePersentage,
            };

            return {
              ...taskdata,
              timeDate: timeDate,
              isSelected: false,
              totalTimeForTask: totalTimeForTask,
              isChanged: true,
              loggedHours: loggedHours,
              estTimePersentage: estTimePersentage,
            };
          } else {
            return taskdata;
          }
        });

        selectedTaskValue.timeSheet = {
          ...project,
          taskData: taskData,
          isChanged: true,
        };
        return { ...project, taskData: taskData, isChanged: true };
      } else {
        return project;
      }
    }) as TimeSheet[];
    setState({
      saveDraft: true,
      userTimeSheetData: userTimeSheetData,
      selectedTaskValue: selectedTaskValue,
    });
  };

  const addTimeEntered_TimeType = (
    rawValue: string,
    selectedTaskValue: SelectedTaskValue,
    entry: TimeEntryCell,
    timeError: boolean,
  ): void => {
    let value = rawValue.replace(/[^0-9.]/g, '');
    value = inputValidation(value, 2);

    timeType_timSlotValueUpdate(value, selectedTaskValue, entry, timeError, 'timeChange');
  };

  /**
   * On blur in the selected-task panel.
   *
   * The error check reads `newTimetotal` off STATE, not off the value just typed — that
   * field was written by the last `timeType_timSlotValueUpdate`, so this is the day's
   * running total from the previous keystroke rather than the current one. Preserved.
   */
  const validateTimeEntered_TimeType = (
    rawValue: string,
    selectedTaskValue: SelectedTaskValue,
    entry: TimeEntryCell,
  ): void => {
    const value = inputValidationOnBlur(rawValue);

    const time = value === '' ? 0 : parseFloat(String(value));
    const errorStatus = gt(stateRef.current.selectedTaskValue?.newTimetotal, 24) ? true : false;

    if (errorStatus) {
      setState({
        clickInput: false,
      });
    } else {
      setState({
        clickInput: true,
      });
    }

    timeType_timSlotValueUpdate(time, selectedTaskValue, entry, errorStatus, 'timeValidation');
  };

  /** Moves the highlight between the time-type inputs of one day. Blocked while `clickInput` is false. */
  const highlightSelectedInputOfTimeType = (
    selectedTaskValue: SelectedTaskValue,
    entry: TimeEntryCell,
  ): void => {
    if (stateRef.current.clickInput) {
      const userTimeSheetDataExit = stateRef.current.userTimeSheetData;
      const userTimeSheetData = userTimeSheetDataExit?.map((project) => {
        if (
          project.gteProjectId ===
          (selectedTaskValue?.timeSheet as TimeSheet | undefined)?.gteProjectId
        ) {
          const taskData = project?.taskData?.map((taskdata) => {
            if (taskdata.taskId === selectedTaskValue.task!.taskId) {
              const timeDate = taskdata?.timeDate?.map((timedate) => {
                if (timedate.date === selectedTaskValue.timedate!.date) {
                  const timeEntry = timedate?.timeEntry?.map((timeentry) => {
                    if (timeentry.timeEntryId === entry.timeEntryId) {
                      return {
                        ...timeentry,
                        isSelected: true,
                      };
                    } else {
                      return { ...timeentry, isSelected: false };
                    }
                  }) as TimeEntryCell[];

                  selectedTaskValue.timedate = {
                    ...timedate,
                    timeEntry: timeEntry.sort(byTimeTypeId),
                    isSelected: false,
                  };
                  return {
                    ...timedate,
                    isSelected: false,

                    timeEntry: timeEntry.sort(byTimeTypeId),
                  };
                } else {
                  return timedate;
                }
              });

              selectedTaskValue.task = {
                ...taskdata,
                timeDate: timeDate,
                isSelected: false,
              };
              return {
                ...taskdata,
                timeDate: timeDate,
                isSelected: false,
              };
            } else {
              return taskdata;
            }
          });

          selectedTaskValue.timeSheet = {
            ...project,
            taskData: taskData,
          };
          return { ...project, taskData: taskData };
        } else {
          return project;
        }
      });
      setState({
        saveDraft: true,
        userTimeSheetData: userTimeSheetData,
        selectedTaskValue: selectedTaskValue,
      });
    }
  };

  const enterInvoiceCommentOfTimeTypeProject = (
    value: string,
    selectedTaskValue: SelectedTaskValue,
    entry: TimeEntryCell,
  ): void => {
    const userTimeSheetDataExit = stateRef.current.userTimeSheetData;

    const userTimeSheetData = userTimeSheetDataExit?.map((project) => {
      if (
        project.gteProjectId ===
        (selectedTaskValue?.timeSheet as TimeSheet | undefined)?.gteProjectId
      ) {
        const taskData = project?.taskData?.map((taskdata) => {
          if (taskdata.taskId === selectedTaskValue.task!.taskId) {
            const timeDate = taskdata?.timeDate?.map((timedate) => {
              if (timedate.date === selectedTaskValue.timedate!.date) {
                const timeEntry = timedate?.timeEntry?.map((timeentry) => {
                  if (timeentry.timeEntryId === entry.timeEntryId) {
                    return {
                      ...timeentry,
                      invoiceComment: value,
                      isChanged: true,
                    };
                  } else {
                    return timeentry;
                  }
                }) as TimeEntryCell[];

                selectedTaskValue.timedate = {
                  ...timedate,
                  isChanged: true,
                  timeEntry: timeEntry.sort(byTimeTypeId),
                };
                return {
                  ...timedate,
                  isChanged: true,
                  timeEntry: timeEntry.sort(byTimeTypeId),
                };
              } else {
                return timedate;
              }
            });

            selectedTaskValue.task = {
              ...taskdata,
              timeDate: timeDate,
              isChanged: true,
            };
            return {
              ...taskdata,
              timeDate: timeDate,
              isSelected: false,
              isChanged: true,
            };
          } else {
            return taskdata;
          }
        });

        selectedTaskValue.timeSheet = {
          ...project,
          taskData: taskData,
          isChanged: true,
        };
        return { ...project, taskData: taskData, isChanged: true };
      } else {
        return project;
      }
    }) as TimeSheet[];
    setState({
      saveDraft: true,
      userTimeSheetData: userTimeSheetData,
      selectedTaskValue: selectedTaskValue,
    });
  };

  /**
   * update "selectedTaskValue" state ->
   * when click time slot of time Type Enable project -> required to display
   *      selected time slots' data in Selected Task Panel
   *
   * The two consecutive `setState` calls are deliberate and load-bearing: the first
   * installs an EMPTY selection with `refresh: false`, the second the real one with
   * `refresh: true`. `SelectedTask` keys off `refresh` to remount its inputs, so
   * collapsing these into one call would leave the previous task's DOM in place and stop
   * the new day's first input from taking focus.
   */
  const setSelectedTaskPanelValue = (
    timeSheet: TimeSheet,
    gteProjectId: number,
    task: TaskData,
    timedate: TimeDateEntry | undefined,
  ): void => {
    if (stateRef.current.clickInput) {
      // Mutated in place; TimeEntryMainPage and the right panel hold this same object.
      const rightPanelDivClassNameProperty = stateRef.current.rightPanelDivClassNameProperty;
      rightPanelDivClassNameProperty.alerts = {
        button: 'accordion-button collapsed',
        div: 'accordion-collapse collapse',
      };
      rightPanelDivClassNameProperty.addTask = {
        button: 'accordion-button collapsed',
        div: 'accordion-collapse collapse',
      };
      rightPanelDivClassNameProperty.selectedTask = {
        button: 'accordion-button',
        div: 'accordion-collapse collapse show',
      };

      setState({
        selectedTaskValue: {
          gteProjectId: 0,
          timeSheet: timeSheet,
          task: {},
          timedate: {},
          newTimetotal: 0,
          refresh: false,
        },
      });
      setState({
        selectedTaskValue: {
          gteProjectId: gteProjectId,
          timeSheet: timeSheet,
          task: task,
          timedate: timedate,
          newTimetotal: 0,
          refresh: true,
        },
        rightPanelDivClassNameProperty: rightPanelDivClassNameProperty,
      });
    }
  };

  const updateStateOfTimeSheetDataList = (userTimeSheetData: TimeSheet[] | undefined): void => {
    setState({
      // Unguarded `.sort`, as in the original — `undefined` here throws rather than
      // silently emptying the grid.
      userTimeSheetData: sortTimeSheets(userTimeSheetData as TimeSheet[]),
      saveDraft: true,
      loading: false,
    });
  };

  const updateStateOfSelectedTaskValue = (selectedTaskValue: SelectedTaskValue): void => {
    setState({
      selectedTaskValue: selectedTaskValue,
    });
  };

  /** Keeps the day-header totals in step with an edit, without refetching them. */
  const updateDayWiseTotal = (date: string, savedTime: number, newTime: number): void => {
    const selectedDate = stateRef.current.selectedDate;
    const days = stateRef.current.selectedDate?.days?.map((day) => {
      if (day.date === date) {
        const exitTimeTotal = parseFloat(String(day.timeTotal));

        const time = exitTimeTotal - savedTime;
        const timeTotal = time + parseFloat(String(newTime));

        return {
          ...day,
          timeTotal: Math.round((timeTotal + Number.EPSILON) * 100) / 100,
          timeError: parseFloat(String(timeTotal)) > 24 ? true : false,
        };
      } else {
        return day;
      }
    });

    setState({
      selectedDate: { ...selectedDate, days: days },
    });
  };

  /* ------------------------------- save / submit ------------------------------- */

  /**
   * Collects the entries to send, and whether any of them also changed a WIP/Done status.
   *
   * The `isChanged` flags gate the walk at all three levels, so an untouched project or
   * task is skipped entirely. The per-entry test is an OR: a changed time OR a changed
   * invoice comment. Note the time half additionally requires the value to differ from
   * `initialTime`, while the comment half does not check `isChanged` at all — so editing a
   * comment back to its original value still sends the entry.
   */
  const getModifiedTimeListAndWIPDoneStatus = (
    userTimeSheetData: TimeSheet[],
  ): { modifiedTime: unknown[]; anyWipDoneChanges: boolean } => {
    const modifiedTime: unknown[] = [];
    let anyWipDoneChanges = false;
    userTimeSheetData?.map((timeSheet) => {
      if (timeSheet.isChanged) {
        timeSheet?.taskData?.map((taskDataObj) => {
          if (taskDataObj.isChanged) {
            const timeEntry: unknown[] = [];
            taskDataObj?.timeDate?.map((timeDateObj) => {
              timeDateObj?.timeEntry?.map((timeEntryObj) => {
                if (
                  (timeEntryObj.isChanged && timeEntryObj?.time !== timeEntryObj?.initialTime) ||
                  timeEntryObj?.initialInvoiceComment !== timeEntryObj?.invoiceComment
                ) {
                  timeEntry.push({
                    timeEntryId: timeEntryObj.timeEntryId,
                    time: timeEntryObj?.timeError ? 0 : parseFloat(String(timeEntryObj?.time)),
                    invoiceComment: timeEntryObj?.invoiceComment,
                  });
                  if (
                    taskDataObj.isChangedWIPStatus &&
                    timeEntryObj?.time !== timeEntryObj?.initialTime
                  ) {
                    anyWipDoneChanges = true;
                  }
                }
              });
            });
            if (timeEntry?.length > 0) {
              modifiedTime.push({
                timeSheetRecordId: taskDataObj.timeSheetRecordId,
                timeEntry: timeEntry,
              });
            }
          }
        });
      }
    });
    return { modifiedTime: modifiedTime, anyWipDoneChanges: anyWipDoneChanges };
  };

  /**
   * Pushes the WIP/Done toggles that accompanied a save.
   *
   * `userStatus` is `"I"`, `"R"`, or literally `false` when the status is neither "WIP" nor
   * "Done" — the `&&` in the original produces the boolean. Reproduced; the upstream has
   * always received that.
   */
  const saveWIPDoneValueChanges = (userTimeSheetData: TimeSheet[]): void => {
    const promises: Promise<{ taskAssignmentId: number; response: unknown }>[] = [];
    userTimeSheetData?.map((timeSheet) => {
      if (timeSheet.isChanged) {
        timeSheet?.taskData?.map((taskDataObj) => {
          if (taskDataObj.isChanged) {
            if (taskDataObj.isChangedWIPStatus) {
              const value = {
                userStatus:
                  taskDataObj?.taskAssignmentStatus === 'WIP'
                    ? 'I'
                    : taskDataObj?.taskAssignmentStatus === 'Done' && 'R',
                lastUpdatedBy: propsRef.current.loggedUserGteEmployeeNo,
                attribute15: 'W-TE-SC',
              };
              promises.push(updateTaskDone(taskDataObj.taskAssignmentId as number, value));
            }
          }
        });
      }
    });

    getResponseAndDoRefresh(promises);
  };

  const getResponseAndDoRefresh = (
    promises: Promise<{ taskAssignmentId: number; response: unknown }>[],
  ): void => {
    Promise.all(promises).then((promise) => {
      let responseArray: { taskAssignmentId: number }[] = [];
      promise?.map((obj) => {
        responseArray = [...responseArray, { taskAssignmentId: obj?.taskAssignmentId }];
      });
      if (responseArray?.length === promises?.length) {
        refreshTimeSheetData({}, 0, 0, true);
      }
    });
  };

  /**
   * Save-as-draft. Also the auto-save that runs on week change, delegate switch and
   * navigation away.
   *
   * The `state.saveDraft` guard is what makes those auto-saves free when nothing changed —
   * without an edit, this is a no-op and no request is sent.
   *
   * `_submitState` is never read. It is kept in position because every call site passes it
   * positionally; the underscore is what exempts it from `noUnusedParameters`.
   */
  const handleSaveDraft = (
    saveDraftState: boolean,
    _submitState: boolean,
    userTimeSheetData: TimeSheet[],
    year: number,
    week: number,
    loggedUserGteEmployeeNo: number,
    operationalUserGteEmployeeNo: number,
  ): void => {
    if (userTimeSheetData?.length > 0 && stateRef.current.saveDraft) {
      const modifiedTimeListAndWIPDoneStatus =
        getModifiedTimeListAndWIPDoneStatus(userTimeSheetData);
      const modifiedTime = modifiedTimeListAndWIPDoneStatus?.modifiedTime;

      setState({
        loading: true,
      });
      saveDraft(
        {
          identification: 'WU/S',
          loggedUserGteEmployeeId: parseInt(String(loggedUserGteEmployeeNo)),
          operationalUserGteEmployeeId: parseInt(String(operationalUserGteEmployeeNo)),
          modifiedTime: modifiedTime,
        },
        year,
        week,
        saveDraftState,
        userTimeSheetData,
        operationalUserGteEmployeeNo,
        modifiedTimeListAndWIPDoneStatus?.anyWipDoneChanges,
      );
    }
  };

  const saveDraft = (
    value: unknown,
    year: number,
    week: number,
    saveDraftState: boolean,
    userTimeSheetData: TimeSheet[],
    operationalUserGteEmployeeNo: number,
    WIPDoneStatus: boolean,
  ): void => {
    saveOrSubmit(
      year,
      week,
      'S',
      value,
      stateRef.current.selectedDate?.currentDate?.toString(),
    )
      .then((response) => {
        propsRef.current.setAlerts(operationalUserGteEmployeeNo);

        /*
         * Only ONE of these runs. saveWIPDoneValueChanges ends in
         * getResponseAndDoRefresh, which calls refreshTimeSheetData itself — so calling
         * both would fire two full refreshes.
         */
        if (WIPDoneStatus) {
          saveWIPDoneValueChanges(userTimeSheetData);
        } else {
          refreshTimeSheetData({}, 0, 0, true);
        }
        setState({
          saveDraft: false,
          // loading: false,
        });

        if (saveDraftState) {
          setState({
            responseMessage: (response as { body?: { message?: string } })?.body?.message as string,
            // showWarningIcon: true,
            response: true,
          });
        }
      })
      .catch((error) => {
        setState({
          loading: false,
          error: toErrorState(error),
        });
      });
  };

  /**
   * Submit for approval.
   *
   * `modifiedTime` is omitted entirely from the body when nothing changed, rather than
   * sent as an empty array — the upstream distinguishes the two.
   */
  const handleSubmitForApproval = (
    userTimeSheetData: TimeSheet[],
    year: number,
    week: number,
    loggedUserGteEmployeeNo: number,
    operationalUserGteEmployeeNo: number,
  ): void => {
    setState({
      loading: true,
    });

    const modifiedTimeListAndWIPDoneStatus = getModifiedTimeListAndWIPDoneStatus(userTimeSheetData);
    const modifiedTime = modifiedTimeListAndWIPDoneStatus?.modifiedTime;
    let value;

    if (modifiedTime?.length > 0) {
      value = {
        identification: 'WU/U',
        loggedUserGteEmployeeId: parseInt(String(loggedUserGteEmployeeNo)),
        operationalUserGteEmployeeId: parseInt(String(operationalUserGteEmployeeNo)),
        modifiedTime: modifiedTime,
      };
    } else if (modifiedTime?.length === 0) {
      value = {
        identification: 'WU/U',
        loggedUserGteEmployeeId: parseInt(String(loggedUserGteEmployeeNo)),
        operationalUserGteEmployeeId: parseInt(String(operationalUserGteEmployeeNo)),
      };
    }

    saveOrSubmit(year, week, 'U', value, stateRef.current.selectedDate?.currentDate?.toString())
      .then((response) => {
        handleSuccessOrErrorStatus(response);
        propsRef.current.setAlerts(operationalUserGteEmployeeNo);
        if (modifiedTimeListAndWIPDoneStatus?.anyWipDoneChanges) {
          saveWIPDoneValueChanges(userTimeSheetData);
        } else {
          refreshTimeSheetData({}, 0, 0, true);
        }
      })
      .catch((error) => {
        setState({
          loading: false,
          error: toErrorState(error),
        });
      });
  };

  /**
   * Should update -> open model and display data -> remains
   *
   * A non-empty response is a list of validation failures and opens the modal; anything
   * else is a success message and raises the toast. Note the `else if` chain: a single
   * response entry carrying two kinds of error only ever surfaces the first.
   *
   * The original took a second `userTimeSheetData` parameter whose only use was commented
   * out (jsx:2198-2200); it is dropped here.
   */
  const handleSuccessOrErrorStatus = (response: unknown): void => {
    const length = (response as { length?: number } | undefined)?.length;

    if (length !== undefined && length > 0) {
      // Mutated in place, then handed back to setState — as in the original.
      const errorModel = stateRef.current.errorModel;
      (response as SubmitErrorEntry[])?.map((message) => {
        if ((message?.maximumHourExceedError?.length ?? 0) > 0) {
          const maximumHourExceedErrorList = getSortedListByDate(message?.maximumHourExceedError);
          errorModel.maximumHourExceedError = {
            error: true,
            errorList: maximumHourExceedErrorList,
          };
        } else if ((message?.minimumHourExceedError?.length ?? 0) > 0) {
          const minimumHourExceedErrorList = getSortedListByDate(message?.minimumHourExceedError);
          errorModel.minimumHourExceedError = {
            error: true,
            errorList: minimumHourExceedErrorList,
          };
        } else if ((message?.invoiceCommentMissingError?.length ?? 0) > 0) {
          const invoiceCommentMissingErrorList = getSortedListByDate(
            message?.invoiceCommentMissingError,
          );
          errorModel.invoiceCommentMissingError = {
            error: true,
            errorList: invoiceCommentMissingErrorList,
          };
        }
      });

      errorModel.openErrorModel = true;
      setState({
        errorModel: errorModel,
      });
    } else {
      setState({
        responseMessage: (response as { message?: string })?.message as string,
        response: true,
      });
    }
  };

  /* ---------------------------- toolbar and panels ---------------------------- */

  /** Opens the calendar. The original computed the negation and then ignored it. */
  const handleCalender = (): void => {
    setState({ openCalender: true });
  };

  const updateStateOfRightPanelDivClassNameProperty = (
    rightPanelDivClassNameProperty: RightPanelDivClassNameProperty,
  ): void => {
    setState({
      rightPanelDivClassNameProperty: rightPanelDivClassNameProperty,
    });
  };

  /**
   * Opens the Alerts section when an alert is clicked in the navbar.
   *
   * The mutation happens BEFORE the `if`, so the shared object is rewritten even when
   * `clickedAlert` is false and no setState follows. The panel therefore shows the alerts
   * section on its next render for any other reason. Reproduced as written.
   */
  const handleNonProjectAlertButton = (): void => {
    const rightPanelDivClassNameProperty = stateRef.current.rightPanelDivClassNameProperty;
    rightPanelDivClassNameProperty.alerts = {
      button: 'accordion-button',
      div: 'accordion-collapse collapse show',
    };
    rightPanelDivClassNameProperty.addTask = {
      button: 'accordion-button collapsed',
      div: 'accordion-collapse collapse',
    };
    rightPanelDivClassNameProperty.selectedTask = {
      button: 'accordion-button collapsed',
      div: 'accordion-collapse collapse',
    };

    if (propsRef.current.clickedAlert) {
      updateStateOfRightPanelDivClassNameProperty(rightPanelDivClassNameProperty);
      propsRef.current.updateStateOfClickedAlert(false);
    }
  };

  const unfreezeButton = (): void => {
    setState({ unfreezeModal: true });
  };

  /**
   * Copy the previous week's billable times forward.
   *
   * Note the asymmetry, preserved: the employee number comes from PROPS while the
   * logged-user number comes from STATE. They differ for one render after a delegate
   * switch, before `setSelectedDelegateUser` commits.
   */
  const copyWeekButton = (): void => {
    setState({ loading: true });

    copyWeek(
      {
        gteEmployeeNo: propsRef.current.operationalUserGteEmployeeNo,
        year: stateRef.current.selectedDate.year,
        weekNo: stateRef.current.selectedDate.week,
        loggedUserGteEmployeeNo: stateRef.current.loggedUserGteEmployeeNo,
      },
      stateRef.current.selectedDate?.currentDate?.toString(),
    )
      .then((response) => {
        setState({
          responseMessage: (response as { body?: { message?: string } })?.body?.message as string,
          response: true,
          loading: false,
        });

        getUserTimeSheetData(stateRef.current.userTimeSheetData);
      })
      .catch((error) => {
        setState({
          loading: false,
          error: toErrorState(error),
        });
      });
  };

  const copyUnassignTaskButton = (): void => {
    setState({ loading: true });

    copyUnassignedTask(
      {
        gteEmployeeNo: propsRef.current.operationalUserGteEmployeeNo,
        year: stateRef.current.selectedDate.year,
        weekNo: stateRef.current.selectedDate.week,
        loggedUserGteEmployeeNo: stateRef.current.loggedUserGteEmployeeNo,
      },
      stateRef.current.selectedDate?.currentDate?.toString(),
    )
      .then((response) => {
        const message = (response as { message?: string })?.message;
        setState({
          responseMessage: message as string,
          response: true,
          loading: false,
        });

        // An exact-string test against the upstream's success sentence. Kept verbatim —
        // it is the only signal that the unassigned-task list needs refetching.
        if (message === 'Tasks Successfully Copied from Previous Week!') {
          setState({ callUnassignTask: true });
        } else {
          setState({ callUnassignTask: false });
        }

        getUserTimeSheetData(stateRef.current.userTimeSheetData);
      })
      .catch((error) => {
        setState({
          loading: false,
          error: toErrorState(error),
        });
      });
  };

  /**
   * The old AddTask handed the whole axios response up and this read `response.data.Status`.
   * `apiClient` returns the parsed body itself, so the `.data` hop is gone — the value read
   * is the same one.
   */
  const addTaskResponse = (response: unknown): void => {
    setState({
      responseMessage: (response as { Status?: string })?.Status as string,
      response: true,
    });
  };

  const setStateOfResponse = (value: boolean): void => {
    setState({
      response: value,
    });
  };

  const updateStateofIsTaskDone = (value: boolean): void => {
    setState({
      isTaskDone: value,
    });
  };

  /* ------------------------------ lifecycle ----------------------------- */

  /**
   * componentDidMount / componentWillUnmount.
   *
   * The teardown calls `addEventListener` rather than `removeEventListener` (jsx:315) —
   * the original's typo, which leaks one "mousedown" listener per unmount. Reproduced,
   * because MainMenu unmounts on logout and the listener it leaks still closes a calendar
   * that is no longer on screen; the leak is invisible but real, and fixing it here would
   * be an unrelated change.
   */
  useLayoutEffect(() => {
    const handleClickOutside = (event: Event): void => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setState({ openCalender: false }); // Call the closeCalendar function from the parent
      }
    };

    getProjectList();
    getEmployeeLeaveHolidayList();
    setSelectedDelegateUser();
    getFreezeDate();
    document.addEventListener('mousedown', handleClickOutside);
    propsRef.current.updateSelectedDate(stateRef.current.selectedDate);

    return () => {
      document.addEventListener('mousedown', handleClickOutside);
    };
    // Mount only, as componentDidMount was.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** componentDidUpdate, branch for branch. */
  useLegacyDidUpdate<TimeEntryControllerProps, TimeEntryState>(
    props,
    state,
    (prevProps, prevState) => {
      /*
       * jsx:176-181 compares freezeDateData.isUpdated against ITSELF, so this branch can
       * never run. Kept so the shape of componentDidUpdate is recognisable next to the
       * original; validateFreezeDate is reached from getFreezeDate and from the
       * week-change branch below, which is what actually drives it.
       */
      if (
        stateRef.current.freezeDateData?.isUpdated !== stateRef.current.freezeDateData?.isUpdated
      ) {
        validateFreezeDate();
      }

      if (props.error !== prevProps.error) {
        setState({
          loading: false,
          initialLoading: false,
          error: props.error,
        });
      }

      /*
       * A delegate switch. The state copy lags the prop by one commit, which is exactly
       * what makes this fire once: handleSaveDraft flushes the OUTGOING user's edits
       * (state still holds their employee number), then the state is caught up and the
       * new user's data is loaded.
       */
      if (stateRef.current.operationalUserGteEmployeeNo !== props.operationalUserGteEmployeeNo) {
        handleSaveDraft(
          false,
          false,
          stateRef.current.userTimeSheetData,
          stateRef.current.selectedDate.year,
          stateRef.current.selectedDate.week,
          stateRef.current.loggedUserGteEmployeeNo,
          stateRef.current.operationalUserGteEmployeeNo,
        );
        setSelectedDelegateUser();
        getProjectList();
        getEmployeeLeaveHolidayList();
      }

      if (prevState.datePicker !== stateRef.current.datePicker) {
        setState({
          projectListIds: [],
          pastWeekLeaveMismatchesDetails: {
            status: '',
            message: '',
            dates: [],
            isUpdated: false,
          },
        });
        selectedDatePicker();
        propsRef.current.setAlerts(stateRef.current.operationalUserGteEmployeeNo);
        setState({
          openCalender: false,
        });
      }

      //when timesheets change set callUnassignTask false
      if (prevState.userTimeSheetData !== stateRef.current.userTimeSheetData) {
        setState({ callUnassignTask: false });
      }

      if (prevState.selectedDate.week !== stateRef.current.selectedDate.week) {
        setState({
          refresh: true,
          projectListIds: [],
          pastWeekLeaveMismatchesDetails: {
            status: '',
            message: '',
            dates: [],
            isUpdated: false,
          },
        });

        const reqDate = stateRef.current.selectedDate.firstDayofCurrentWeek;
        const dateOfNextMonday = moment(reqDate).format('YYYY-MM-DD');
        //if maxDate < navigate date then disable next week button
        if (
          setDateAsNumber(getMaximumNavigateDate()) <= setDateAsNumber(new Date(dateOfNextMonday))
        ) {
          setState({ disableNextBtn: true });
        } else {
          setState({ disableNextBtn: false });
        }

        // Saves the week being LEFT — hence prevState's year and week.
        handleSaveDraft(
          false,
          false,
          stateRef.current.userTimeSheetData,
          prevState.selectedDate.year,
          prevState.selectedDate.week,
          stateRef.current.loggedUserGteEmployeeNo,
          stateRef.current.operationalUserGteEmployeeNo,
        );
        getFreezeDate();
        getPastWeekLeaveMismatches();

        getProjectList();
        getEmployeeLeaveHolidayList();
        if (stateRef.current.freezeDateData.isUpdated) {
          validateFreezeDate();
        }
      }

      if (
        stateRef.current.projectList.length !== 0 &&
        prevState.projectList !== stateRef.current.projectList
      ) {
        getProjectIds();
      }

      // One-shot: NavBar raises the flag, the latch stops it repeating every render.
      if (props.handleSaveDraftRequested && !stateRef.current.isHandleSaveDraftCalled) {
        handleSaveDraft(
          false,
          false,
          stateRef.current.userTimeSheetData,
          stateRef.current.selectedDate.year,
          stateRef.current.selectedDate.week,
          stateRef.current.loggedUserGteEmployeeNo,
          stateRef.current.operationalUserGteEmployeeNo,
        );
        setState({ isHandleSaveDraftCalled: true });
      }

      if (prevProps.clickedAlert !== props.clickedAlert) {
        handleNonProjectAlertButton();
      }

      if (
        stateRef.current.selectedDate.firstDayofCurrentWeek !==
        prevState.selectedDate.firstDayofCurrentWeek
      ) {
        propsRef.current.updateSelectedDate(stateRef.current.selectedDate);
      }
    },
  );

  return {
    state,
    setState,
    calendarRef,

    pastWeek,
    nextWeek,
    handleCalender,
    unfreezeButton,
    copyWeekButton,
    copyUnassignTaskButton,
    handleSaveDraft,
    handleSubmitForApproval,

    timSlotValueUpdate,
    setSelectedTaskPanelValue,
    updateStateOfTimeSheetDataList,
    updateSelectedTaskValue,
    updateStateOfRightPanelDivClassNameProperty,
    updateStateofIsTaskDone,
    refreshTimeSheetData,

    addTimeEntered_TimeType,
    validateTimeEntered_TimeType,
    highlightSelectedInputOfTimeType,
    enterInvoiceCommentOfTimeTypeProject,

    addTaskResponse,
    setStateOfResponse,
  };
}

export type TimeEntryController = ReturnType<typeof useTimeEntryController>;
